import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Navbar,
  NavDropdown,
  Form,
  Button,
  Alert
} from 'react-bootstrap';
import Icon from '../common/Icons';
import { useToast } from '../../hooks/useToast';
import { ReceptionToastStack } from './ui';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';
import receptionService from '../../services/receptionService';
import { ROLES } from '../../constants/roles';
import { getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import { ORIENT_LABELS, ORIENT_SERVICE } from './receptionConstants';
import { formatReceptionHeaderMs } from './receptionFormatters';
import { RECEP_NAV_ITEMS } from './receptionPaths';
import ReceptionGuideModal from './ReceptionGuideModal';
import { ReceptionNavIcon } from './ui';
import './ReceptionistDashboard.css';

const SIDEBAR_STORAGE_KEY = 'recv-sidebar-collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function ReceptionMobileNavStrip({ items }) {
  return (
    <nav
      className="d-lg-none recv-mobile-nav recv-mobile-nav--reception border-bottom flex-shrink-0 recv-no-print"
      aria-label="Navigation rapide"
    >
      <Container fluid className="recv-mobile-nav--reception__container">
        <div className="recv-mobile-nav__track">
          {items.map(({ to, end, label, shortLabel }) => (
            <NavLink
              key={to}
              to={to}
              end={!!end}
              title={label}
              className={({ isActive }) =>
                `recv-mobile-pill recv-mobile-pill--reception${isActive ? ' is-active' : ''}`
              }
            >
              {shortLabel || label}
            </NavLink>
          ))}
        </div>
      </Container>
    </nav>
  );
}

function ReceptionNavMenu({ items, onNavigate, collapsed = false }) {
  return (
    <div className="recv-menu-section">
      <div className="recv-menu-section__items">
        {items.map(({ to, end, label, hint, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) => `recv-menu-item${isActive ? ' is-active' : ''}`}
          >
            <span className="recv-menu-item__icon" aria-hidden>
              <ReceptionNavIcon name={icon} />
            </span>
            <span className="recv-menu-item__text">
              <span className="recv-menu-item__label">{label}</span>
              {hint ? <span className="recv-menu-item__hint">{hint}</span> : null}
            </span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function ReceptionistLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;

  const { toasts, showToast, removeToast, getDuration } = useToast();
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [defaultOrientation, setDefaultOrientation] = useState('DENTAL');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [receptionSites, setReceptionSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [activeReceptionSite, setActiveReceptionSite] = useState(null);

  const [headerClock, setHeaderClock] = useState('Synchronisation…');
  const serverSkewMsRef = useRef(0);
  const serverDisplayTzRef = useRef('');

  const canUseReceptionApi =
    user?.role === ROLES.RECEPTIONIST ||
    user?.role === ROLES.ADMIN ||
    user?.role === ROLES.ADMIN_PERSONNEL;

  const canEditReceptionDefaults =
    user?.role === ROLES.ADMIN || user?.role === ROLES.ADMIN_PERSONNEL;

  const receptionSiteSessionKey = useMemo(() => {
    const userKey = user?.id || user?.email || user?.username || 'anonymous';
    return `recv-active-site:${userKey}`;
  }, [user?.email, user?.id, user?.username]);

  const toast = useCallback(
    (variant, msg) => {
      showToast(msg, variant);
    },
    [showToast]
  );

  const loadReceptionSettings = useCallback(async () => {
    if (!canUseReceptionApi) return;
    try {
      const res = await receptionService.getSettings();
      if (res.success && res.data?.default_orientation) {
        setDefaultOrientation(res.data.default_orientation);
      }
    } catch (e) {
      console.error(e);
    }
  }, [canUseReceptionApi]);

  useEffect(() => {
    loadReceptionSettings();
  }, [loadReceptionSettings]);

  const loadReceptionSites = useCallback(async () => {
    if (!canUseReceptionApi) return;
    setSitesLoading(true);
    setSitesError('');
    try {
      const { sites } = await authService.getProfile();
      setReceptionSites(Array.isArray(sites) ? sites : []);
    } catch (error) {
      setReceptionSites([]);
      setSitesError(error.message || 'Impossible de charger vos sites de travail');
    } finally {
      setSitesLoading(false);
    }
  }, [canUseReceptionApi]);

  useEffect(() => {
    loadReceptionSites();
  }, [loadReceptionSites]);

  useEffect(() => {
    if (!canUseReceptionApi || sitesLoading || activeReceptionSite || receptionSites.length === 0) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(receptionSiteSessionKey);
      if (raw) {
        const stored = JSON.parse(raw);
        const restoredSite = receptionSites.find((site) => String(site.id) === String(stored?.id));
        if (restoredSite) {
          setActiveReceptionSite(restoredSite);
          return;
        }
        sessionStorage.removeItem(receptionSiteSessionKey);
      }
    } catch {
      sessionStorage.removeItem(receptionSiteSessionKey);
    }

    if (receptionSites.length === 1) {
      const onlySite = receptionSites[0];
      setActiveReceptionSite(onlySite);
      try {
        sessionStorage.setItem(receptionSiteSessionKey, JSON.stringify({ id: onlySite.id }));
      } catch {
        /* ignore */
      }
    }
  }, [
    activeReceptionSite,
    canUseReceptionApi,
    receptionSiteSessionKey,
    receptionSites,
    sitesLoading
  ]);

  useEffect(() => {
    if (!canUseReceptionApi) return undefined;

    let cancelled = false;

    async function sync() {
      try {
        const t0 = Date.now();
        const res = await receptionService.getServerTime();
        const t1 = Date.now();
        if (cancelled || !res.success || !res.data) return;
        const raw = res.data.unixMs;
        const serverMs = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        if (Number.isNaN(serverMs)) return;
        const rttHalf = Math.floor((t1 - t0) / 2);
        serverSkewMsRef.current = serverMs + rttHalf - Date.now();
        serverDisplayTzRef.current =
          typeof res.data.displayTimeZone === 'string' ? res.data.displayTimeZone.trim() : '';

        const nowAdjusted = Date.now() + serverSkewMsRef.current;
        setHeaderClock(formatReceptionHeaderMs(nowAdjusted, serverDisplayTzRef.current || undefined));
      } catch (e) {
        console.error(e);
        if (!cancelled)
          setHeaderClock((prev) => (prev === 'Synchronisation…' ? 'Horloge indisponible' : prev));
      }
    }

    sync();
    const syncInterval = window.setInterval(sync, 120000);
    const tickInterval = window.setInterval(() => {
      setHeaderClock(
        formatReceptionHeaderMs(
          Date.now() + serverSkewMsRef.current,
          serverDisplayTzRef.current || undefined
        )
      );
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(syncInterval);
      window.clearInterval(tickInterval);
    };
  }, [canUseReceptionApi]);

  const outletContext = useMemo(
    () => ({
      toast,
      defaultOrientation,
      activeReceptionSite,
      setActiveReceptionSite
    }),
    [toast, defaultOrientation, activeReceptionSite]
  );

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileNav();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(receptionSiteSessionKey);
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const saveDefaultOrientation = async () => {
    try {
      const res = await receptionService.putSettings({ default_orientation: defaultOrientation });
      if (res.success) {
        toast('success', 'Orientation par défaut enregistrée (dentiste / médecine / infirmerie).');
      }
    } catch (err) {
      toast('danger', err.message || 'Enregistrement impossible');
    }
  };

  const chooseReceptionSite = (site) => {
    setActiveReceptionSite(site);
    try {
      sessionStorage.setItem(receptionSiteSessionKey, JSON.stringify({ id: site.id }));
    } catch {
      /* sessionStorage may be unavailable in restricted contexts */
    }
    toast('success', `Site d'accueil actif : ${site.name}`);
  };

  const resetReceptionSite = () => {
    setActiveReceptionSite(null);
    try {
      sessionStorage.removeItem(receptionSiteSessionKey);
    } catch {
      /* ignore */
    }
    closeMobileNav();
  };

  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() ||
    (user?.name || 'R').charAt(0).toUpperCase();

  const displayName =
    `${user?.last_name || ''}${user?.first_name ? ` ${user.first_name}` : ''}`.trim() ||
    user?.name ||
    'Compte réception';

  const layoutClassName = [
    'receptionist-dashboard',
    'receptionist-layout',
    'min-vh-100',
    'd-flex',
    'flex-column',
    sidebarCollapsed ? 'recv-sidebar-is-collapsed' : '',
    mobileNavOpen ? 'recv-mobile-nav-open' : ''
  ]
    .filter(Boolean)
    .join(' ');

  if (!canUseReceptionApi) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center p-4">
        <Alert variant="danger" className="recv-dash-card border-0 shadow">
          Ce compte ne dispose pas de l&apos;espace réception.
        </Alert>
      </div>
    );
  }

  return (
    <div className={layoutClassName}>
      <Navbar variant="dark" className="recv-topbar recv-no-print flex-shrink-0 shadow-sm">
        <Container fluid className="recv-topbar__container">
          {activeReceptionSite ? (
            <button
              type="button"
              className={`recv-topbar__toggle d-lg-none navbar-toggler ${mobileNavOpen ? 'is-open' : ''}`}
              aria-controls="recv-mobile-drawer"
              aria-expanded={mobileNavOpen}
              aria-label={mobileNavOpen ? 'Fermer le menu réception' : 'Ouvrir le menu réception'}
              onClick={() => (mobileNavOpen ? closeMobileNav() : openMobileNav())}
            >
              {mobileNavOpen ? (
                <Icon name="times" size={20} className="recv-topbar__toggle-icon" aria-hidden />
              ) : (
                <span className="navbar-toggler-icon" />
              )}
            </button>
          ) : null}

          <Navbar.Brand
            className="recv-topbar__brand-wrap mb-0"
            onClick={() => {
              closeMobileNav();
              navigate('/receptionist');
            }}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
              alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
              className="recv-topbar__wordmark"
            />
            <span className="recv-topbar__brand">
              <span className="recv-topbar__brand-product">{ACCENT}</span>
              <span className="recv-topbar__brand-section">Réception</span>
            </span>
          </Navbar.Brand>

          <div className="recv-topbar__toolbar ms-auto d-flex align-items-center gap-2">
            {activeReceptionSite ? (
              <button
                type="button"
                className="recv-site-chip"
                onClick={resetReceptionSite}
                title="Changer le site d'accueil"
              >
                <span className="recv-site-chip__label">Site</span>
                <span className="recv-site-chip__name">{activeReceptionSite.name}</span>
              </button>
            ) : null}
            <span
              className="recv-topbar__clock d-none d-md-inline recv-header-clock"
              title="Heure référencée sur le serveur (MySQL NOW), resynchronisée toutes les 2 min"
              aria-live="polite"
            >
              {headerClock}
            </span>
            <NavDropdown
              align="end"
              className="recv-user-menu"
              title={
                <span className="recv-user-menu__trigger" aria-label="Menu du compte réception">
                  <span className="recv-user-menu__avatar" aria-hidden>
                    {initials}
                  </span>
                  <span className="recv-user-menu__meta d-none d-md-flex">
                    <span className="recv-user-menu__name">{displayName}</span>
                    <span className="recv-user-menu__role">{getRoleLabel(user?.role)}</span>
                  </span>
                  <Icon name="chevronDown" size={14} className="recv-user-menu__chevron d-none d-md-block" aria-hidden />
                </span>
              }
              id="recv-dd"
            >
              <NavDropdown.Header className="recv-user-menu__header">
                <span className="recv-user-menu__header-avatar" aria-hidden>
                  {initials}
                </span>
                <span className="recv-user-menu__header-text">
                  <span className="recv-user-menu__header-name">{displayName}</span>
                  {user?.email ? <span className="recv-user-menu__header-email">{user.email}</span> : null}
                  <span className="recv-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
                </span>
              </NavDropdown.Header>
              <NavDropdown.Divider className="recv-user-menu__divider" />
              <NavDropdown.Item className="recv-user-menu__action" onClick={() => setShowGuideModal(true)}>
                Guide d&apos;accueil
              </NavDropdown.Item>
              {activeReceptionSite ? (
                <NavDropdown.Item className="recv-user-menu__action" onClick={resetReceptionSite}>
                  Changer de site
                </NavDropdown.Item>
              ) : null}
              <NavDropdown.Divider className="recv-user-menu__divider" />
              <NavDropdown.Item className="recv-user-menu__action recv-user-menu__action--danger" onClick={handleLogout}>
                <Icon name="signOut" size={16} aria-hidden />
                Déconnexion
              </NavDropdown.Item>
            </NavDropdown>
          </div>
        </Container>
      </Navbar>

      {activeReceptionSite ? <ReceptionMobileNavStrip items={RECEP_NAV_ITEMS} /> : null}

      {activeReceptionSite && mobileNavOpen ? (
        <>
          <button
            type="button"
            className="recv-mobile-drawer__backdrop recv-no-print"
            aria-label="Fermer le menu réception"
            onClick={closeMobileNav}
          />
          <aside
            id="recv-mobile-drawer"
            className="recv-mobile-drawer recv-no-print"
            role="dialog"
            aria-modal="true"
            aria-label="Menu réception"
          >
            <div className="recv-mobile-drawer__head">
              <div className="recv-mobile-drawer__head-text">
                <span className="recv-mobile-drawer__title">Menu réception</span>
                {activeReceptionSite ? (
                  <span className="recv-mobile-drawer__site">{activeReceptionSite.name}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="recv-mobile-drawer__close"
                aria-label="Fermer"
                onClick={closeMobileNav}
              >
                <Icon name="times" size={18} />
              </button>
            </div>
            <nav className="recv-sidebar__nav recv-mobile-drawer__nav" aria-label="Navigation principale">
              <ReceptionNavMenu items={RECEP_NAV_ITEMS} onNavigate={closeMobileNav} />
            </nav>
            {canEditReceptionDefaults ? (
              <div className="recv-settings-block recv-mobile-drawer__settings px-3 py-3 small">
                <div className="fw-semibold text-muted text-uppercase mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                  Orientation par défaut
                </div>
                <Form.Select
                  className="mb-2"
                  size="sm"
                  value={defaultOrientation}
                  onChange={(ev) => setDefaultOrientation(ev.target.value)}
                >
                  <option value={ORIENT_SERVICE.DENTAL}>{ORIENT_LABELS[ORIENT_SERVICE.DENTAL]}</option>
                  <option value={ORIENT_SERVICE.MEDICAL}>{ORIENT_LABELS[ORIENT_SERVICE.MEDICAL]}</option>
                  <option value={ORIENT_SERVICE.NURSING}>{ORIENT_LABELS[ORIENT_SERVICE.NURSING]}</option>
                </Form.Select>
                <Button variant="primary" size="sm" className="w-100" onClick={saveDefaultOrientation}>
                  Enregistrer
                </Button>
              </div>
            ) : null}
            <button
              type="button"
              className="recv-mobile-drawer__guide"
              onClick={() => {
                closeMobileNav();
                setShowGuideModal(true);
              }}
            >
              Guide d&apos;accueil
            </button>
          </aside>
        </>
      ) : null}

      {!activeReceptionSite ? (
        <main className="recv-site-gate flex-grow-1 overflow-auto">
          <Container fluid className="recv-site-gate__container">
            <section className="recv-site-gate__panel" aria-labelledby="recv-site-gate-title">
              <div className="recv-site-gate__intro">
                <span className="recv-site-gate__badge">Ouverture de session réception</span>
                <h1 id="recv-site-gate-title">Choisir votre site de travail</h1>
                <p>
                  Sélectionnez le site d&apos;accueil où vous travaillez maintenant parmi ceux affectés à votre
                  compte. Les visites et orientations de cette session utiliseront ce site par défaut.
                </p>
              </div>

              {sitesLoading ? (
                <div className="recv-site-gate__state">
                  <span className="spinner-border spinner-border-sm" aria-hidden />
                  Chargement des sites...
                </div>
              ) : sitesError ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  <span>{sitesError}</span>
                  <Button variant="outline-primary" size="sm" onClick={loadReceptionSites}>
                    Réessayer
                  </Button>
                </div>
              ) : receptionSites.length === 0 ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  Aucun site de travail n&apos;est affecté à votre profil. Contactez un administrateur pour vous
                  attribuer au moins un site actif.
                </div>
              ) : (
                <div className="recv-site-grid" role="list">
                  {receptionSites.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      className="recv-site-card"
                      onClick={() => chooseReceptionSite(site)}
                      role="listitem"
                    >
                      <span className="recv-site-card__icon" aria-hidden>
                        <Icon name="building" size={18} />
                      </span>
                      <span className="recv-site-card__body">
                        <span className="recv-site-card__name">{site.name}</span>
                        <span className="recv-site-card__meta">
                          {site.address || site.location || 'Site de réception'}
                        </span>
                      </span>
                      <span className="recv-site-card__cta">Commencer</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </Container>
        </main>
      ) : (
      <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
        <aside
          className={`recv-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
            sidebarCollapsed ? ' is-collapsed' : ''
          }`}
          aria-label="Navigation réception"
        >
          <div className="recv-sidebar__brand">
            <div className="recv-sidebar__brand-label">Espace</div>
            <div className="recv-sidebar__brand-title">Accueil &amp; orientation</div>
          </div>

          <nav className="recv-sidebar__nav" aria-label="Navigation principale">
            <ReceptionNavMenu items={RECEP_NAV_ITEMS} collapsed={sidebarCollapsed} />
          </nav>

          <div className="recv-sidebar__guide recv-no-print">
            <Button
              variant="outline-secondary"
              size="sm"
              className="w-100 recv-btn-ghost"
              onClick={() => setShowGuideModal(true)}
            >
              Guide d&apos;accueil
            </Button>
          </div>

          {canEditReceptionDefaults && (
            <div className="recv-settings-block px-3 py-3 small">
              <div className="fw-semibold text-muted text-uppercase mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                Orientation par défaut
              </div>
              <div className="text-muted lh-sm mb-2" style={{ fontSize: '0.8125rem' }}>
                Pré-sélection à l&apos;ouverture de la fenêtre « Orienter ».
              </div>
              <Form.Select
                className="mb-2"
                size="sm"
                value={defaultOrientation}
                onChange={(ev) => setDefaultOrientation(ev.target.value)}
              >
                <option value={ORIENT_SERVICE.DENTAL}>{ORIENT_LABELS[ORIENT_SERVICE.DENTAL]}</option>
                <option value={ORIENT_SERVICE.MEDICAL}>{ORIENT_LABELS[ORIENT_SERVICE.MEDICAL]}</option>
                <option value={ORIENT_SERVICE.NURSING}>{ORIENT_LABELS[ORIENT_SERVICE.NURSING]}</option>
              </Form.Select>
              <Button variant="primary" size="sm" className="w-100" onClick={saveDefaultOrientation}>
                Enregistrer
              </Button>
            </div>
          )}

          <div className="recv-sidebar__footer">
            <button
              type="button"
              className="recv-sidebar__toggle"
              onClick={toggleSidebar}
              aria-expanded={!sidebarCollapsed}
              aria-label={sidebarCollapsed ? 'Déplier le menu réception' : 'Replier le menu réception'}
            >
              <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={18} aria-hidden />
              <span className="recv-sidebar__toggle-label">
                {sidebarCollapsed ? 'Déplier' : 'Replier'}
              </span>
            </button>
          </div>
        </aside>

        <main className="recv-main flex-grow-1 overflow-auto">
          <Container fluid className="py-4 px-md-4 recv-dash-container">
            <Outlet context={outletContext} />
          </Container>
        </main>
      </div>
      )}

      <ReceptionGuideModal show={showGuideModal} onHide={() => setShowGuideModal(false)} />

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
